/** Tiny inline icons; keeps the public bundle free of an icon library. */
interface IconProps { className?: string }

const base = { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true } as const;

export const PhoneIcon = ({ className }: IconProps) => (
  <svg {...base} className={className}><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3-8.7A2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8 9.8a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.9 2z"/></svg>
);
export const MailIcon = ({ className }: IconProps) => (
  <svg {...base} className={className}><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/></svg>
);
export const GlobeIcon = ({ className }: IconProps) => (
  <svg {...base} className={className}><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15 15 0 0 1 0 20 15 15 0 0 1 0-20z"/></svg>
);
export const PinIcon = ({ className }: IconProps) => (
  <svg {...base} className={className}><path d="M12 22s7-7.1 7-12a7 7 0 1 0-14 0c0 4.9 7 12 7 12z"/><circle cx="12" cy="10" r="2.5"/></svg>
);
export const LinkIcon = ({ className }: IconProps) => (
  <svg {...base} className={className}><path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1"/><path d="M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1"/></svg>
);
export const ClockIcon = ({ className }: IconProps) => (
  <svg {...base} className={className}><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
);
export const CalendarIcon = ({ className }: IconProps) => (
  <svg {...base} className={className}><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
);
export const CheckIcon = ({ className }: IconProps) => (
  <svg {...base} className={className}><path d="M20 6 9 17l-5-5"/></svg>
);
export const ChevronIcon = ({ className, dir = 'left' }: IconProps & { dir?: 'left' | 'right' }) => (
  <svg {...base} className={className}>{dir === 'left' ? <path d="m15 18-6-6 6-6"/> : <path d="m9 18 6-6-6-6"/>}</svg>
);

/** Small neutral payment symbols. Zelle is intentionally represented by a
 * bank-transfer mark rather than the Zelle logo, whose stylized mark is
 * licensed separately. */
export function PaymentMethodIcon({ method }: { method: 'cash' | 'card' | 'zelle' }) {
  if (method === 'cash') {
    return <svg {...base} className="payment-method-icon" aria-hidden="true"><rect x="3" y="6" width="18" height="12" rx="2"/><circle cx="12" cy="12" r="2.5"/><path d="M7 9H6v1M17 9h1v1M7 15H6v-1M17 15h1v-1"/></svg>;
  }
  if (method === 'card') {
    return <svg {...base} className="payment-method-icon" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 10h18M7 15h4"/></svg>;
  }
  return <svg {...base} className="payment-method-icon" aria-hidden="true"><path d="m3 10 9-6 9 6"/><path d="M5 10v9M9 10v9M15 10v9M19 10v9M3 19h18"/></svg>;
}

/** Social networks get a recognisable brand glyph; anything else a link icon.
 * The marks stay monochrome so they work in both themes and do not imply a
 * partnership or endorsement. */
export function SocialIcon({ network }: { network: string }) {
  const n = network.toLowerCase();
  if (n.includes('instagram')) return <svg {...base} className="social-icon social-icon--instagram"><rect x="3" y="3" width="18" height="18" rx="5"/><circle cx="12" cy="12" r="4.25"/><circle cx="17.4" cy="6.6" r="1" fill="currentColor" stroke="none"/></svg>;
  if (n.includes('facebook')) return <svg {...base} className="social-icon social-icon--facebook"><path d="M13.8 21v-8h2.8l.4-3h-3.2V8.1c0-.9.3-1.6 1.7-1.6H17V3.8c-.3 0-1.2-.1-2.4-.1-2.4 0-4.1 1.5-4.1 4.2V10H8v3h2.5v8h3.3z" fill="currentColor" stroke="none"/></svg>;
  if (n === 'x' || n.includes('twitter')) return <svg {...base} className="social-icon social-icon--x"><path d="M5 4l14 16M19 4 5 20"/></svg>;
  if (n.includes('tiktok')) return <svg {...base} className="social-icon social-icon--tiktok"><path d="M14 4v10a4 4 0 1 1-4-4"/><path d="M14 4c0 3 2 5 5 5"/></svg>;
  if (n.includes('linkedin')) return <svg {...base} className="social-icon social-icon--linkedin"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M8 10v7M8 7v.5M12 17v-4a2 2 0 0 1 4 0v4M12 10v7"/></svg>;
  if (n.includes('youtube')) return <svg {...base} className="social-icon social-icon--youtube"><rect x="3" y="6" width="18" height="12" rx="3"/><path d="m10 9 5 3-5 3z" fill="currentColor" stroke="none"/></svg>;
  if (n.includes('whatsapp')) return <svg {...base} className="social-icon social-icon--whatsapp"><path d="M20 11.5a8 8 0 0 1-11.8 7L4 20l1.5-4A8 8 0 1 1 20 11.5z"/><path d="M9 8.5c.5 2 2 3.5 4 4l1-1 2 1c-.5 2-1.5 2.5-3 2-3-.9-5.6-3.5-6.5-6.5-.5-1.5 0-2.5 2-3l1 2-1 1z"/></svg>;
  return <LinkIcon />;
}
